import { useState, useEffect, useCallback } from 'react';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { isParagraphCodeMode, toggleParagraphCodeMode } from '@/lib/paragraphCodeMode';

/**
 * Floating badge shown when paragraph-code-mode is active.
 *
 * Activating the mode:
 *   URL param    ?pp_codes=1  (persists to localStorage, survives page refresh)
 *   URL param    ?pp_codes=0  (clears localStorage)
 *   Click badge  toggles the mode on/off
 *
 * When active, every getContent() call returns «PPCode» instead of resolved text,
 * so QE and implementation teams can identify which DB entry drives each string.
 */
export function ParagraphCodeModeBadge() {
  const [active, setActive] = useState(isParagraphCodeMode);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'DisplayParagraphConfigMapping') setActive(isParagraphCodeMode());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const handleToggle = useCallback(() => {
    const next = toggleParagraphCodeMode();
    setActive(next);
  }, []);

  if (!active) return null;

  return (
    <Tooltip
      title="Paragraph code mode ON — getContent() shows codes instead of text. Click to disable."
      placement="left"
    >
      <Chip
        label="«ParagraphConfig Codes»"
        size="small"
        onClick={handleToggle}
        sx={{
          position: 'fixed',
          bottom: 72,
          insetInlineEnd: 16,
          zIndex: 9999,
          fontFamily: 'monospace',
          fontWeight: 700,
          fontSize: '0.7rem',
          letterSpacing: '0.04em',
          bgcolor: '#f59e0b',
          color: '#1c1917',
          border: '2px solid #d97706',
          boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          cursor: 'pointer',
          '&:hover': { bgcolor: '#fbbf24' },
        }}
      />
    </Tooltip>
  );
}
